import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Alert, Table, Badge } from 'react-bootstrap';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker } from '@react-google-maps/api';
import type { Library } from '@googlemaps/js-api-loader';
import Swal from 'sweetalert2';
import { Booking, MergedGroup } from '../booking';
import UpdateMergedBookingsModal from './UpdateMergedBookingsModal';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import FeatherIcon from 'feather-icons-react';
import ViewVehicleBookings from './ViewVehicleBookings';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';

interface ViewMergedBookingsModalProps {
  show: boolean;
  onHide: () => void;
  groupId: string | null;
  statusColors: { [key: string]: string };
  onUpdateSuccess: () => void;
}

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  'AIzaSyC7S0DKmKpZmtidyMto2PiIXhR5miu9Pi0';
const GOOGLE_MAPS_LIBRARIES: Library[] = ['places'];
const center = { lat: 6.9271, lng: 79.8612 };

const ViewMergedBookingsModal: React.FC<ViewMergedBookingsModalProps> = ({
  show,
  onHide,
  groupId,
  statusColors,
  onUpdateSuccess,
}) => {
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [mergedGroup, setMergedGroup] = useState<MergedGroup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isServiceManager, setIsServiceManager] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (!show) {
      setDirections(null); // Reset so that next open triggers route
    }
  }, [show]);

  useEffect(() => {
    setIsServiceManager(checkPermission('ROLE_SERVICE_MANAGER'));
  }, [show]);

  useEffect(() => {
    const fetchMergedGroup = async () => {
      if (!show || !groupId) {
        setMergedGroup(null);
        setError('No group ID provided.');
        return;
      }
      try {
        setError(null);
        const response = await apiDefinitions.getMergeGroupDetailsById(groupId);
        setMergedGroup(response.data.data);
      } catch (err: any) {
        const message = err.response?.data?.statusDescription || err.message || 'Failed to fetch merged group details.';
        setError(message);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: message,
        });
      }
    };
    fetchMergedGroup();
  }, [show, groupId]);

  const formatDate = (input: number | string | null | undefined): string => {
    if (!input) return 'N/A';
    let date: Date;
    if (typeof input === 'number') {
      date = new Date(input);
    } else {
      date = new Date(Number(input));
    }
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return date.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const renderDestinations = (
    locations: MergedGroup['mergeGroupLocation']
  ): JSX.Element => {
    if (!locations || locations.length === 0) {
      return <tr><td colSpan={2}>No destinations available</td></tr>;
    }
    return (
      <>
        {locations.map((location: NonNullable<MergedGroup['mergeGroupLocation']>[0], index: number) => (
          <tr key={`${location.id}-${index}`}>
            <td>{location.orderId}</td>
            <td
              title={location.address || 'N/A'}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '250px',
              }}
            >
              {location.address
                ? location.address.length > 40
                  ? `${location.address.slice(0, 40)}...`
                  : location.address
                : 'N/A'}
            </td>
          </tr>
        ))}
      </>
    );
  };

  const calculateRoute = useCallback(
    async (locations: MergedGroup['mergeGroupLocation']) => {
      if (!isLoaded || !locations || locations.length < 2) {
        return;
      }
      const validLocations = locations.filter((loc: NonNullable<MergedGroup['mergeGroupLocation']>[0]) => loc.lat && loc.lng);
      if (validLocations.length < 2) {
        setError('Insufficient valid locations for route calculation.');
        return;
      }
      const directionsService = new google.maps.DirectionsService();
      const waypoints = validLocations
        .slice(1, -1)
        .map((loc: NonNullable<MergedGroup['mergeGroupLocation']>[0]) => ({
          location: new google.maps.LatLng(parseFloat(loc.lat), parseFloat(loc.lng)),
          stopover: true,
        }));
      const origin = validLocations[0];
      const destination = validLocations[validLocations.length - 1];
      try {
        const result = await directionsService.route({
          origin: new google.maps.LatLng(parseFloat(origin.lat), parseFloat(origin.lng)),
          destination: new google.maps.LatLng(parseFloat(destination.lat), parseFloat(destination.lng)),
          waypoints,
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: true,
        });
        setDirections(result);
        if (mapRef.current && result.routes[0]) {
          const bounds = new google.maps.LatLngBounds();
          result.routes[0].overview_path.forEach((point) => bounds.extend(point));
          mapRef.current.fitBounds(bounds);
        }
      } catch (error: any) {
       
        setError(`Failed to calculate route: ${error.message}`);
      }
    },
    [isLoaded, groupId]
  );

  const getTotalEstimatedKMs = (bookings: Booking[] | null | undefined): number => {
    if (!Array.isArray(bookings)) {
      return 0;
    }
    const total = bookings.reduce((sum, booking) => sum + (Number(booking.estimatedKms) || 0), 0);
    return total;
  };

  const getTotalPassengers = (bookings: Booking[] | null | undefined): number => {
    if (!Array.isArray(bookings)) {
      return 0;
    }
    const total = bookings.reduce((sum, booking) => sum + (Number(booking.noOfPassengers) || 0), 0);
    return total;
  };

  const handleUnmerge = async () => {
    if (!mergedGroup?.groupId) {
      setError('No merged group selected.');
      return;
    }

    const result = await Swal.fire({
      title: 'Confirm Unmerge',
      text: `Are you sure you want to unmerge group ID ${mergedGroup.groupId}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Unmerge',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
    });

    if (result.isConfirmed) {
      try {
        await apiDefinitions.unmergeVehicleBookings(mergedGroup.groupId);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Merged group unmerged successfully.',
          timer: 2000,
          showConfirmButton: false,
        });
        onUpdateSuccess();
        setTimeout(onHide, 2000);
      } catch (err: any) {
        const message = err.response?.data?.statusDescription || err.message || 'Failed to unmerge group.';
        setError(message);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: message,
        });
      }
    }
  };

  useEffect(() => {
    if (show && mergedGroup && Array.isArray(mergedGroup.mergeGroupLocation)) {
      calculateRoute(mergedGroup.mergeGroupLocation);
    }
  }, [show, isLoaded, mergedGroup, calculateRoute]);

  if (loadError) {
    return (
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        autoFocus={false}
        enforceFocus={false}
        aria-labelledby={`merged-group-${groupId || 'unknown'}`}
      >
        <Modal.Header closeButton>
          <Modal.Title id={`merged-group-${groupId || 'unknown'}`}>
            Merged Group Details - Group ID: {groupId || 'N/A'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-danger">Error loading Google Maps: {loadError.message}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        autoFocus={false}
        enforceFocus={false}
        aria-labelledby={`merged-group-${groupId || 'unknown'}`}
      >
        <Modal.Header closeButton>
          <Modal.Title id={`merged-group-${groupId || 'unknown'}`}>
            Merged Group Details - Group ID: {groupId || 'N/A'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {!mergedGroup && <Alert variant="info">Loading merged group details...</Alert>}
          {mergedGroup && (
            <div className="container-fluid">
              {/* Bookings Table */}
              <div className="row">
                <div className="col-md-12">
                  <h5>Bookings</h5>
                  {mergedGroup.bookings && mergedGroup.bookings.length > 0 ? (
                    <Table bordered hover>
                      <thead>
                        <tr>
                          <th>Booking ID</th>
                          <th>User</th>
                          <th>Status</th>
                          <th>Departure</th>
                          <th>Arrival</th>
                          <th>Passengers</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mergedGroup.bookings.map((booking) => (
                          <tr key={booking.id}>
                            <td>{booking.id}</td>
                            <td>{booking.user?.name || 'N/A'} ({booking.user?.empNumber || 'N/A'})</td>
                            <td>
                              <Badge bg={statusColors[booking.status] || 'secondary'}>
                                {booking.status || 'N/A'}
                              </Badge>
                            </td>
                            <td>{formatDate(booking.departureTime)}</td>
                            <td>{formatDate(booking.arrivalTime)}</td>
                            <td>{booking.noOfPassengers || 'N/A'}</td>
                             <td>
                              <Button
                              className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                              size="sm"
                              onClick={() => {
                                setSelectedBooking(booking);
                                setShowViewModal(true);
                              }}
                              aria-label={`View booking ${booking.id}`}
                              title="View booking details"
                            >
                              <FeatherIcon icon="eye" size={16} />
                            </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <Alert variant="info">No bookings available.</Alert>
                  )}
                </div>
              </div>
              {/* Destinations Table and Map Side by Side */}
              <div className="row">
                <div className="col-md-6">
                  <h5>Destinations</h5>
                  <Table bordered hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Address</th>
                      </tr>
                    </thead>
                    <tbody>{renderDestinations(mergedGroup.mergeGroupLocation)}</tbody>
                  </Table>
                </div>
                <div className="col-md-6">
                  <h5>Route Map</h5>
                  {isLoaded ? (
                    <GoogleMap
                      mapContainerStyle={{ height: '400px', width: '100%' }}
                      center={center}
                      zoom={12}
                      onLoad={(map) => {
                        mapRef.current = map;
                      }}
                    >
                      {mergedGroup?.mergeGroupLocation?.map((location: NonNullable<MergedGroup['mergeGroupLocation']>[0], index: number) =>
                        location.lat && location.lng ? (
                          <Marker
                            key={`${location.id}-${index}`}
                            position={{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }}
                            title={`Location ${location.orderId}`}
                          />
                        ) : null
                      )}
                      {directions && (
                        <DirectionsRenderer
                          directions={directions}
                          options={{
                            polylineOptions: {
                              strokeColor: '#1976D2',
                              strokeOpacity: 0.8,
                              strokeWeight: 5,
                            },
                          }}
                        />
                      )}
                    </GoogleMap>
                  ) : (
                    <div>Loading Google Maps...</div>
                  )}
                </div>
              </div>
              {/* Summary Information */}
              <div className="row mt-3">
                <div className="col-md-12">
                  <div>
                    <strong>Total Estimated KMs:</strong> {getTotalEstimatedKMs(mergedGroup.bookings).toFixed(2)} km
                  </div>
                  <div className="mt-2">
                    <strong>Total Passengers:</strong> {getTotalPassengers(mergedGroup.bookings)}
                  </div>
                  <div className="mt-2">
                    <strong>Suggested Departure Time:</strong>{' '}
                    {mergedGroup.suggestedDepartureTime ? formatDate(Number(mergedGroup.suggestedDepartureTime)) : 'N/A'}
                  </div>
                  <div className="mt-2">
                    <strong>Suggested Arrival Time:</strong>{' '}
                    {mergedGroup.suggestedArrivalTime ? formatDate(Number(mergedGroup.suggestedArrivalTime)) : 'N/A'}
                  </div>
                  <div className="mt-2">
                    <strong>Vehicle Suggestions:</strong>{' '}
                    {mergedGroup.vehicleSuggestions?.length
                      ? mergedGroup.vehicleSuggestions.map((v) => v.numberPlate).join(', ')
                      : 'N/A'}
                  </div>
                  <div className="mt-2">
                    <strong>Driver Suggestions:</strong>{' '}
                    {mergedGroup.driverSuggestions?.length
                      ? mergedGroup.driverSuggestions.map((d) => d.empName).join(', ')
                      : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {isServiceManager && (
            <>
              <Button
                variant="outline-primary"
                onClick={() => setShowUpdateModal(true)}
                aria-label="Update merged group"
              >
                <FeatherIcon icon="edit" size={18} /> Update
              </Button>
              <Button
                variant="outline-danger"
                onClick={handleUnmerge}
                aria-label="Unmerge group"
              >
                <FeatherIcon icon="x-circle" size={18} /> Unmerge
              </Button>
            </>
          )}
          <Button
            variant="primary"
            onClick={onHide}
            aria-label="Close merged group modal"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {isServiceManager && mergedGroup && (
        <UpdateMergedBookingsModal
          show={showUpdateModal}
          onHide={() => setShowUpdateModal(false)}
          mergedGroup={mergedGroup}
          onUpdateSuccess={onUpdateSuccess}
        />
        
      )}
      {selectedBooking && (
  <ViewVehicleBookings
    show={showViewModal}
    onHide={() => {
      setShowViewModal(false);
      setSelectedBooking(null);
    }}
    booking={selectedBooking}
    statusColors={statusColors}
  />
)}

    </>
  );
};

export default ViewMergedBookingsModal;